/**
 * Share Utilities
 * URL generation, clipboard, and Web Share API helpers
 */

import { logger } from './logger';

/**
 * Generate a shareable URL for a memory
 * @param {string} memoryId - The memory UUID
 * @returns {string} The full shareable URL
 */
export function getShareableUrl(memoryId) {
  return `${window.location.origin}/m/${memoryId}`;
}

/**
 * Copy text to clipboard
 * @param {string} text - The text to copy
 * @returns {Promise<boolean>} True if successful
 */
export async function copyToClipboard(text) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    const success = document.execCommand('copy');
    document.body.removeChild(textArea);
    return success;
  } catch (err) {
    logger.error('Failed to copy to clipboard:', err);
    return false;
  }
}

/**
 * Share a memory using the best available method
 * @param {Object} options - Share options
 * @param {string} options.url - The share URL
 * @param {string} options.title - The share title
 * @param {string} options.text - The share text/description
 * @param {Blob} [options.imageBlob] - Optional image blob to share
 * @returns {Promise<{method: string, success: boolean}>} Result with method used
 */
export async function shareMemory({ url, title, text, imageBlob }) {
  // Try native share with image first (mobile)
  if (imageBlob && navigator.canShare) {
    try {
      const file = new File([imageBlob], 'memory.png', { type: 'image/png' });
      const shareData = { title, text, url, files: [file] };

      if (navigator.canShare(shareData)) {
        await navigator.share(shareData);
        return { method: 'native-with-image', success: true };
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        logger.warn('Native share with image failed:', err);
      } else {
        // User cancelled
        return { method: 'cancelled', success: false };
      }
    }
  }

  // Try native share without image
  if (navigator.share) {
    try {
      await navigator.share({ title, text, url });
      return { method: 'native', success: true };
    } catch (err) {
      if (err.name !== 'AbortError') {
        logger.warn('Native share failed:', err);
      } else {
        // User cancelled
        return { method: 'cancelled', success: false };
      }
    }
  }

  // Fallback to clipboard
  const copied = await copyToClipboard(url);
  return { method: 'clipboard', success: copied };
}

/**
 * Download a blob as a file
 * @param {Blob} blob - The blob to download
 * @param {string} filename - The filename
 */
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Check if native sharing is available
 * @returns {boolean}
 */
export function canUseNativeShare() {
  return !!navigator.share;
}

/**
 * Check if native sharing with files is available
 * @returns {boolean}
 */
export function canShareFiles() {
  if (!navigator.canShare) return false;

  try {
    const testFile = new File(['test'], 'test.png', { type: 'image/png' });
    return navigator.canShare({ files: [testFile] });
  } catch {
    return false;
  }
}
